import { useEffect, useState } from 'react';
import { apiClient, axiosClient } from '../../../auth-lib';
import { Button, Card, Input, Select, Modal, StatusPill, EmptyState, Skeleton, useToast } from '../../../design-system';

const PRIORITIES = ['low', 'medium', 'high'];
const STATUSES = ['open', 'in_progress', 'fulfilled', 'closed'];
const dateOnly = (v) => (v ? v.slice(0, 10) : '');
const EMPTY = { title: '', description: '', priority: 'medium', dueDate: '', assignedUsers: [] };

// UI for the ClientRequest backend (docs/info asks made of a client). Uses the
// nested list route GET /api/clients/:id/requests plus the top-level
// /api/client-requests mutations. Supports create, status change, comments
// (internal vs shared-with-client), and spawning a task from a request.
function ClientRequestsManager({ clientId }) {
  const toast = useToast();
  const [requests, setRequests] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [detail, setDetail] = useState(null); // currently-open request
  const [comment, setComment] = useState({ text: '', visibility: 'internal' });

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/api/clients/${clientId}/requests`);
      setRequests(res.data);
    } catch (err) {
      // non-fatal
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    axiosClient.get('/users').then((r) => setOrgUsers(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [clientId]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, clientId };
      if (!payload.dueDate) delete payload.dueDate;
      await apiClient.post('/api/client-requests', payload);
      setCreateOpen(false);
      setForm(EMPTY);
      toast.success('Request created');
      fetchRequests();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create request');
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (id, status) => {
    try {
      const res = await apiClient.put(`/api/client-requests/${id}`, { status });
      setRequests((prev) => prev.map((r) => (r._id === id ? res.data : r)));
      if (detail?._id === id) setDetail(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to update status');
    }
  };

  const addComment = async () => {
    if (!comment.text.trim()) return;
    try {
      const res = await apiClient.post(`/api/client-requests/${detail._id}/comments`, comment);
      setDetail((prev) => ({ ...prev, comments: res.data }));
      setComment({ text: '', visibility: 'internal' });
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to add comment');
    }
  };

  const spawnTask = async (id) => {
    try {
      await apiClient.post(`/api/client-requests/${id}/tasks`, {});
      toast.success('Task created from request');
      fetchRequests();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to create task');
    }
  };

  const priorityVariant = (p) => (p === 'high' ? 'danger' : p === 'low' ? 'info' : 'warning');

  return (
    <div className="tab-content">
      <Card>
        <Card.Header title="Client Requests" action={<Button size="sm" onClick={() => { setForm(EMPTY); setError(''); setCreateOpen(true); }}>+ New Request</Button>} />
        {loading ? (
          <Skeleton.Text lines={3} />
        ) : requests.length === 0 ? (
          <EmptyState icon="📨" title="No requests yet" description="Raise a request for documents or information from this client." />
        ) : (
          <div className="enrollments-list">
            {requests.map((r) => (
              <div key={r._id} className="enrollment-card">
                <div className="enrollment-info">
                  <div className="enrollment-name-row">
                    <button className="client-name-link" onClick={() => setDetail(r)}><strong>{r.title}</strong></button>
                    <StatusPill variant={priorityVariant(r.priority)} label={r.priority} />
                  </div>
                  <div className="enrollment-meta">
                    {r.dueDate && <span>Due: {dateOnly(r.dueDate)}</span>}
                    {r.assignedUsers?.length > 0 && <span>Assigned: {r.assignedUsers.map((u) => u.name).join(', ')}</span>}
                    <span>{r.comments?.length || 0} comment{(r.comments?.length || 0) === 1 ? '' : 's'}</span>
                  </div>
                </div>
                <div className="enrollment-actions">
                  <Select value={r.status} onChange={(e) => changeStatus(r._id, e.target.value)} className="inline-select">
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                  </Select>
                  <Button variant="secondary" size="sm" onClick={() => spawnTask(r._id)}>Spawn Task</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Create modal */}
      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New Client Request"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={create} loading={saving}>Create</Button>
          </>
        }
      >
        <form className="client-form" onSubmit={create}>
          {error && <div className="form-error" role="alert">{error}</div>}
          <Input label="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Input label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="form-row">
            <Select label="Priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </Select>
            <Input label="Due Date" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
          </div>
          <Select
            label="Assign To"
            multiple
            value={form.assignedUsers}
            onChange={(e) => setForm({ ...form, assignedUsers: Array.from(e.target.selectedOptions, (o) => o.value) })}
          >
            {orgUsers.map((u) => <option key={u._id} value={u._id}>{u.name}</option>)}
          </Select>
        </form>
      </Modal>

      {/* Detail + comments modal */}
      <Modal
        isOpen={!!detail}
        onClose={() => setDetail(null)}
        title={detail?.title || 'Request'}
        footer={<Button variant="secondary" onClick={() => setDetail(null)}>Close</Button>}
      >
        {detail && (
          <div className="client-form">
            {detail.description && <p>{detail.description}</p>}
            <div className="enrollment-meta">
              <StatusPill status={detail.status === 'open' ? 'active' : 'inactive'} label={detail.status} />
              <StatusPill variant={priorityVariant(detail.priority)} label={detail.priority} />
              {detail.dueDate && <span>Due: {dateOnly(detail.dueDate)}</span>}
            </div>

            <h4>Comments</h4>
            {(detail.comments || []).length === 0 ? (
              <p className="hint-text">No comments yet.</p>
            ) : (
              <div className="comments-list">
                {detail.comments.map((c) => (
                  <div key={c._id} className="comment-item">
                    <div className="comment-head">
                      <strong>{c.authorId?.name || 'User'}</strong>
                      <StatusPill variant={c.visibility === 'internal' ? 'info' : 'success'} label={c.visibility === 'internal' ? 'Internal' : 'Shared with client'} />
                      <span className="hint-text">{dateOnly(c.createdAt)}</span>
                    </div>
                    <p>{c.text}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="form-row" style={{ alignItems: 'flex-end' }}>
              <Input label="Add comment" value={comment.text} onChange={(e) => setComment({ ...comment, text: e.target.value })} />
              <Select label="Visibility" value={comment.visibility} onChange={(e) => setComment({ ...comment, visibility: e.target.value })}>
                <option value="internal">Internal</option>
                <option value="shared_with_client">Shared with client</option>
              </Select>
              <Button variant="primary" onClick={addComment}>Post</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default ClientRequestsManager;
